import type { CommonPowerSyncDatabase, PowerSyncBackendConnector } from '@powersync/common';
import { waitForLocalDataWipe } from './localDataWipe';
import { powersyncLogger } from './logger';

/** Attempts before giving up until something mounts the provider again. */
const MAX_ATTEMPTS = 3;

/** Breathing room before re-attempting, so an unforeseen cause cannot turn this into a hot loop. */
const RETRY_DELAY_MS = 500;

/**
 * The client schema this build expects, sent as the `schema_version` connection parameter. The sync
 * streams choose what each row syncs as from it: `'2'` gets inventory under the names Postgres uses
 * since MDC-3686 — `inventories`, `inventoriables`, `inventory_location`, their `inventory` columns
 * and the `inventory` type value — while a build from before that sends nothing and keeps the
 * `expense` ones. Bump it, and add a query per version to `sync-streams.yaml`, when something
 * synced is renamed again.
 */
export const SYNC_SCHEMA_VERSION = '2';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type ConnectVerifiedOptions = {
  database: CommonPowerSyncDatabase;
  connector: PowerSyncBackendConnector;
  /** Reported to PowerSync as connection metadata. */
  appVersion: string;
  /** Checked around every await so an unmount stops the loop instead of connecting behind it. */
  isCancelled: () => boolean;
};

/**
 * Connects, then confirms the SDK actually took the request.
 *
 * `connect()` resolves in two situations that look identical to the caller. Either a sync stream was
 * created and an attempt completed — `connected` is true, or `downloadError` is set and the SDK's
 * own retry loop owns recovery from there — or nothing happened at all: `ConnectionManager`
 * abandons the attempt when its pending options were cleared by a concurrent `disconnect()`, which
 * is what a sign-out wipe does, and returns before a sync stream is ever created. That second case
 * is silent and leaves nothing running to retry, so the status has to be inspected afterwards.
 *
 * Checking straight after the await is enough: the SDK resolves `connect()` only once a real
 * attempt has settled, so a genuine one always has one of those three flags set by then.
 */
export const connectVerified = async ({
  database,
  connector,
  appVersion,
  isCancelled,
}: ConnectVerifiedOptions): Promise<void> => {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    // An in-progress wipe is the usual reason an attempt gets abandoned, so wait it out first.
    await waitForLocalDataWipe();

    if (isCancelled()) {
      return;
    }

    powersyncLogger.info('connecting', { appVersion, attempt });
    await database.connect(connector, {
      appMetadata: {
        app_version: appVersion,
      },
      params: {
        schema_version: SYNC_SCHEMA_VERSION,
      },
    });

    const status = database.currentStatus;
    if (status.connected || status.connecting || status.downloadError) {
      return;
    }

    if (isCancelled()) {
      return;
    }

    powersyncLogger.warn('connect was abandoned before a sync stream existed, retrying', {
      attempt,
      maxAttempts: MAX_ATTEMPTS,
    });

    await delay(RETRY_DELAY_MS);
  }

  // Nothing else will retry: the provider connects on mount, and this is that call. Reported rather
  // than left silent because the app is now running fully offline with no path back.
  powersyncLogger.error('could not establish a sync connection', { attempts: MAX_ATTEMPTS });
};
